/**
 * Browser session store for the member bearer session.
 *
 * Storage posture (interim, documented in the PR): the access token lives in
 * memory only; the refresh token and the signup-channel record live in
 * `sessionStorage`, so a registration survives a same-tab reload but never
 * outlives the tab. The static-export S3 hosting offers no server to set an
 * HttpOnly cookie — the upgrade path is a CloudFront/BFF session layer, at
 * which point this module is the only file that changes.
 */

export interface SessionTokens {
  accessToken: string;
  refreshToken?: string;
  /** Epoch milliseconds at which the access token expires. */
  expiresAtEpochMs?: number;
}

/** Map a server token bundle onto the stored shape, stamping absolute expiry at receipt. */
export function toSessionTokens(bundle: {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}): SessionTokens {
  return {
    accessToken: bundle.access_token,
    refreshToken: bundle.refresh_token,
    expiresAtEpochMs: Date.now() + bundle.expires_in * 1000,
  };
}

const REFRESH_TOKEN_KEY = 'galapagos.auth.refresh_token';
const SIGNUP_CHANNEL_KEY = 'galapagos.auth.signup_channel';

let accessToken: string | null = null;

type SessionListener = () => void;
const listeners = new Set<SessionListener>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** Subscribe to session changes (sign-in, refresh, sign-out). */
export function onSessionChange(listener: SessionListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Every sessionStorage touch is guarded: the accessor itself can throw in
// privacy modes, and a thrown read must degrade to "no session", never crash.
function storageGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Session continues in memory only; a reload will require a fresh OTP.
  }
}

function storageRemove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Nothing to do — the value was unreadable anyway.
  }
}

export function getAuthToken(): string | null {
  return accessToken;
}

export function getRefreshToken(): string | null {
  return storageGet(REFRESH_TOKEN_KEY);
}

export function hasSession(): boolean {
  return accessToken !== null || getRefreshToken() !== null;
}

export function persistSessionTokens(tokens: SessionTokens): void {
  accessToken = tokens.accessToken;
  if (tokens.refreshToken !== undefined) {
    storageSet(REFRESH_TOKEN_KEY, tokens.refreshToken);
  }
  notify();
}

/**
 * Swap in a refreshed bundle only when the refresh token on record is still
 * the one the refresh was minted from — a concurrent sign-out (or a
 * replacement sign-in) must not be clobbered by a slow refresh landing late.
 */
export function replaceSessionTokensIfRefreshUnchanged(
  expectedRefreshToken: string,
  tokens: SessionTokens,
): boolean {
  if (getRefreshToken() !== expectedRefreshToken) return false;
  persistSessionTokens(tokens);
  return true;
}

export function clearSessionTokens(): void {
  accessToken = null;
  storageRemove(REFRESH_TOKEN_KEY);
  storageRemove(SIGNUP_CHANNEL_KEY);
  notify();
}

/**
 * The OTP channel this session verified with — what the registration contact
 * screen's inverse rule reads. Kept alongside the tokens because it shares
 * their lifetime exactly.
 */
export type SignupChannel = 'email' | 'phone';

export function persistSignupChannel(channel: SignupChannel): void {
  storageSet(SIGNUP_CHANNEL_KEY, channel);
}

export function getSignupChannel(): SignupChannel | null {
  const value = storageGet(SIGNUP_CHANNEL_KEY);
  return value === 'email' || value === 'phone' ? value : null;
}
